import { zodResolver } from "@hookform/resolvers/zod"
import { useQuery } from "@tanstack/react-query"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "design-system/atoms/Accordion"
import { Badge, BadgeProps } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "design-system/atoms/Sheet"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { Combobox } from "design-system/organisms/Combobox"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { ExternalLink, Plus, Rocket, Trash2 } from "lucide-react"
import {
  getBackofficePromptExperimentOptionsQuery,
  getBackofficePromptExperimentRunsQuery,
  useLaunchBackofficePromptExperimentMutation,
} from "modules/backoffice/lib/experiments/promptExperiments"
import { useCallback, useEffect, useState } from "react"
import { useFieldArray, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import {
  BackofficePromptExperimentOptionsResponseDataSchema,
  LaunchBackofficePromptExperimentSchema,
  PromptExperimentRunStatusEnum,
} from "schema"
import { toast } from "sonner"
import { z } from "zod"

type FormValues = z.infer<typeof LaunchBackofficePromptExperimentSchema>
type PromptOption = z.infer<
  typeof BackofficePromptExperimentOptionsResponseDataSchema
>["prompts"][number]

type Props = {
  onClose: () => void
}

const statusVariant: Record<(typeof PromptExperimentRunStatusEnum)[number], BadgeProps["variant"]> =
  {
    Queued: "secondary",
    Running: "warning",
    Completed: "success",
    Failed: "negative",
  }

export function PromptExperimentSheet({ onClose }: Props) {
  const { t } = useTranslation()
  const { data: options } = useQuery(getBackofficePromptExperimentOptionsQuery())
  const { data: runs } = useQuery(getBackofficePromptExperimentRunsQuery())
  const [promptSearchQuery, setPromptSearchQuery] = useState("")
  const [advancedSettings, setAdvancedSettings] = useState("")
  const launchMutation = useLaunchBackofficePromptExperimentMutation()
  const form = useForm<FormValues>({
    resolver: zodResolver(LaunchBackofficePromptExperimentSchema),
    defaultValues: {
      name: "",
      promptName: "",
      sample: 300,
      concurrency: 5,
      reruns: 1,
      experiments: [],
    },
  })
  const { append, fields, remove, replace } = useFieldArray({
    control: form.control,
    name: "experiments",
  })

  const selectPrompt = useCallback(
    (prompt: PromptOption) => {
      form.setValue("promptName", prompt.promptName, { shouldValidate: true })
      replace(getDefaultExperiments(prompt))
    },
    [form, replace],
  )

  useEffect(() => {
    const firstPrompt = options?.prompts[0]
    if (!firstPrompt || form.getValues("promptName")) return
    selectPrompt(firstPrompt)
  }, [form, options, selectPrompt])

  async function onSubmit(values: FormValues) {
    await launchMutation.mutateAsync(values)
    toast.success(t("backoffice.conversationExperiments.promptExperiment.success"))
  }

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="flex h-full w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl">
        <SheetHeader className="shrink-0 border-b bg-muted/40 p-5 text-left">
          <SheetTitle>{t("backoffice.conversationExperiments.promptExperiment.title")}</SheetTitle>
          <SheetDescription>
            {t("backoffice.conversationExperiments.promptExperiment.description")}
          </SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          <Form
            {...form}
            onSubmit={form.handleSubmit(onSubmit)}
            errorTranslations={getErrorTranslations()}
            className="gap-5"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("backoffice.conversationExperiments.promptExperiment.form.name")}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      placeholder={t(
                        "backoffice.conversationExperiments.promptExperiment.form.namePlaceholder",
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-[minmax(0,1fr)_8rem] gap-4">
              <FormField
                control={form.control}
                name="promptName"
                render={({ field }) => {
                  const promptOptions =
                    options?.prompts.map((prompt) => ({
                      value: prompt.promptName,
                      label: prompt.promptName,
                    })) ?? []
                  const selectedPrompt = promptOptions.find(
                    (prompt) => prompt.value === field.value,
                  )

                  return (
                    <FormItem>
                      <FormLabel>
                        {t("backoffice.conversationExperiments.promptExperiment.form.prompt")}
                      </FormLabel>
                      <FormControl>
                        <Combobox
                          options={promptOptions}
                          value={selectedPrompt}
                          onChange={(selected) => {
                            const prompt = options?.prompts.find(
                              (item) => item.promptName === selected?.value,
                            )
                            if (prompt) selectPrompt(prompt)
                          }}
                          placeholder={t(
                            "backoffice.conversationExperiments.promptExperiment.form.promptPlaceholder",
                          )}
                          emptyPlaceholder={t(
                            "backoffice.conversationExperiments.promptExperiment.form.promptEmpty",
                          )}
                          searchPlaceholder={t(
                            "backoffice.conversationExperiments.promptExperiment.form.promptSearch",
                          )}
                          searchQuery={promptSearchQuery}
                          setSearchQuery={setPromptSearchQuery}
                          className="w-full"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )
                }}
              />
              <NumberField form={form} name="sample" min={1} max={1000} />
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-medium text-base">
                  {t("backoffice.conversationExperiments.promptExperiment.form.variants")}
                </h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => append({ model: "OpenAI_Gpt56Luna" })}
                >
                  <Plus className="size-4" />
                  {t("backoffice.conversationExperiments.promptExperiment.form.addVariant")}
                </Button>
              </div>

              {fields.map((experiment, index) => (
                <div
                  key={experiment.id}
                  className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-end gap-3 rounded-md border p-3 bg-muted-foreground/10"
                >
                  <FormField
                    control={form.control}
                    name={`experiments.${index}.model`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t("backoffice.conversationExperiments.promptExperiment.form.model")}
                        </FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {options?.models.map((model) => (
                              <SelectItem key={model.model} value={model.model}>
                                {model.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name={`experiments.${index}.reasoningEffort`}
                    render={({ field }) => {
                      const modelName = form.watch(`experiments.${index}.model`)
                      const model = options?.models.find((item) => item.model === modelName)
                      return (
                        <FormItem>
                          <FormLabel>
                            {t(
                              "backoffice.conversationExperiments.promptExperiment.form.reasoning",
                            )}
                          </FormLabel>
                          <Select
                            value={field.value ?? "default"}
                            onValueChange={(value) =>
                              field.onChange(value === "default" ? undefined : value)
                            }
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="default">
                                {t(
                                  "backoffice.conversationExperiments.promptExperiment.form.default",
                                )}
                              </SelectItem>
                              {model?.reasoningEfforts.map((effort) => (
                                <SelectItem key={effort} value={effort}>
                                  {effort}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={fields.length === 1}
                    onClick={() => remove(index)}
                    aria-label={t(
                      "backoffice.conversationExperiments.promptExperiment.form.removeVariant",
                    )}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex items-start gap-3">
                <Accordion
                  type="single"
                  collapsible
                  value={advancedSettings}
                  onValueChange={setAdvancedSettings}
                  className="w-full flex-1 rounded-md"
                >
                  <AccordionItem value="advanced-settings">
                    <AccordionTrigger className="p-0 pb-3 text-xs font-normal justify-start">
                      {t(
                        "backoffice.conversationExperiments.promptExperiment.form.advancedSettings",
                      )}
                    </AccordionTrigger>
                    <AccordionContent className="grid grid-cols-3 gap-4 px-0">
                      <NumberField form={form} name="concurrency" min={1} max={20} />
                      <NumberField form={form} name="reruns" min={1} max={20} />
                      <FormField
                        control={form.control}
                        name="datasetTag"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t(
                                "backoffice.conversationExperiments.promptExperiment.form.datasetTag",
                              )}
                            </FormLabel>
                            <FormControl>
                              <Input {...field} value={field.value ?? ""} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
                {advancedSettings !== "advanced-settings" && (
                  <LaunchExperimentButton
                    isPending={launchMutation.isPending}
                    label={t("backoffice.conversationExperiments.promptExperiment.form.launch")}
                  />
                )}
              </div>
              {advancedSettings === "advanced-settings" && (
                <div className="flex justify-end">
                  <LaunchExperimentButton
                    isPending={launchMutation.isPending}
                    label={t("backoffice.conversationExperiments.promptExperiment.form.launch")}
                  />
                </div>
              )}
            </div>
          </Form>

          <div className="mt-8 flex flex-col gap-3 border-t pt-5">
            <div className="text-md font-medium">
              {t("backoffice.conversationExperiments.promptExperiment.recent.title")}
            </div>
            {runs?.length ? (
              runs.map((run) => (
                <div
                  key={run.id}
                  className="flex items-center justify-between gap-3 rounded-md border p-3"
                >
                  <div className="min-w-0">
                    {run.langsmithUrl ? (
                      <a
                        href={run.langsmithUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1 font-mono text-sm hover:underline"
                      >
                        <span className="truncate">{run.name}</span>
                        <ExternalLink className="size-3 shrink-0" />
                      </a>
                    ) : (
                      <p className="truncate font-mono text-sm">{run.name}</p>
                    )}
                    <div className="mt-1 text-xs text-muted-foreground">
                      <DateDisplay date={run.createdAt} onlyDate />
                    </div>
                  </div>
                  <Badge variant={statusVariant[run.status]}>
                    {t(
                      `backoffice.conversationExperiments.promptExperiment.status.${run.status.toLowerCase()}`,
                    )}
                  </Badge>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">
                {t("backoffice.conversationExperiments.promptExperiment.recent.empty")}
              </p>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}

type NumberFieldProps = {
  form: ReturnType<typeof useForm<FormValues>>
  name: "sample" | "concurrency" | "reruns"
  min: number
  max: number
}

function NumberField({ form, name, min, max }: NumberFieldProps) {
  const { t } = useTranslation()
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {t(`backoffice.conversationExperiments.promptExperiment.form.${name}`)}
          </FormLabel>
          <FormControl>
            <Input
              type="number"
              min={min}
              max={max}
              {...field}
              value={field.value ?? ""}
              onChange={(event) => field.onChange(event.target.valueAsNumber)}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

function LaunchExperimentButton({ isPending, label }: { isPending: boolean; label: string }) {
  return (
    <Button type="submit" disabled={isPending}>
      <Rocket className="size-4" />
      {label}
    </Button>
  )
}

function getDefaultExperiments(prompt: PromptOption): FormValues["experiments"] {
  return [
    {
      model: prompt.model,
      ...(prompt.reasoningEffort && { reasoningEffort: prompt.reasoningEffort }),
      ...(prompt.verbosity && { verbosity: prompt.verbosity }),
    },
  ]
}
